import { useEffect, useRef, useState } from "react"

export function useTabNotification() {
    const [originalTitle] = useState(document.title)
    const [originalFavicon] = useState(() => {
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
        return link?.href || "/favicon.ico"
    })

    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const faviconIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const listenersRef = useRef<Array<{ element: Document | Window; event: string; handler: EventListener }>>([])
    const isFlashingRef = useRef(false)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    const createFaviconWithDot = (showDot: boolean): string => {
        if (!canvasRef.current) {
            canvasRef.current = document.createElement("canvas")
            canvasRef.current.width = 32
            canvasRef.current.height = 32
        }

        const canvas = canvasRef.current
        const ctx = canvas.getContext("2d")!

        ctx.clearRect(0, 0, 32, 32)

        return new Promise<string>((resolve) => {
            const img = new Image()
            img.crossOrigin = "anonymous"

            img.onload = () => {
                ctx.drawImage(img, 0, 0, 32, 32)

                if (showDot) {
                    ctx.beginPath()
                    ctx.arc(24, 8, 8, 0, 2 * Math.PI)
                    ctx.fillStyle = "#ff0000"
                    ctx.fill()
                }

                resolve(canvas.toDataURL())
            }

            img.onerror = () => {
                ctx.fillStyle = "#4a90e2"
                ctx.fillRect(0, 0, 32, 32)

                if (showDot) {
                    ctx.beginPath()
                    ctx.arc(24, 8, 6, 0, 2 * Math.PI)
                    ctx.fillStyle = "#ff0000"
                    ctx.fill()

                    ctx.beginPath()
                    ctx.arc(24, 8, 6, 0, 2 * Math.PI)
                    ctx.strokeStyle = "#ffffff"
                    ctx.lineWidth = 1
                    ctx.stroke()
                }

                resolve(canvas.toDataURL())
            }

            img.src = originalFavicon
        }) as any
    }

    const updateFavicon = async (showDot: boolean) => {
        try {
            const faviconUrl = await createFaviconWithDot(showDot)

            // Remove existing favicon
            const existingLink = document.querySelector("link[rel*='icon']")
            if (existingLink) {
                existingLink.remove()
            }

            // Add new favicon
            const link = document.createElement("link")
            link.rel = "icon"
            link.type = "image/png"
            link.href = faviconUrl
            document.head.appendChild(link)
        } catch (error) {
            console.warn("Could not update favicon:", error)
        }
    }

    const restoreOriginalFavicon = () => {
        const existingLink = document.querySelector("link[rel*='icon']")
        if (existingLink) {
            existingLink.remove()
        }

        const link = document.createElement("link")
        link.rel = "icon"
        link.type = "image/x-icon"
        link.href = originalFavicon
        document.head.appendChild(link)
    }

    const stopFlashing = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }

        if (faviconIntervalRef.current) {
            clearInterval(faviconIntervalRef.current)
            faviconIntervalRef.current = null
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }

        listenersRef.current.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler)
        })
        listenersRef.current = []

        document.title = originalTitle
        restoreOriginalFavicon()
        isFlashingRef.current = false
    }

    const flashTitle = (message: string) => {
        stopFlashing()
        isFlashingRef.current = true

        let isTitleFlashing = true
        intervalRef.current = setInterval(() => {
            document.title = isTitleFlashing ? message : originalTitle
            isTitleFlashing = !isTitleFlashing
        }, 1000)

        let isFaviconFlashing = true
        updateFavicon(true)
        faviconIntervalRef.current = setInterval(() => {
            updateFavicon(isFaviconFlashing)
            isFaviconFlashing = !isFaviconFlashing
        }, 800)

        const handleVisibilityChange = () => {
            if (!document.hidden && isFlashingRef.current) {
                stopFlashing()
            }
        }

        const handleClick = () => {
            if (isFlashingRef.current) {
                stopFlashing()
            }
        }

        const handleFocus = () => {
            if (isFlashingRef.current) {
                stopFlashing()
            }
        }

        const events = [
            { element: document, event: "visibilitychange", handler: handleVisibilityChange },
            { element: document, event: "click", handler: handleClick },
            { element: window, event: "focus", handler: handleFocus },
        ]

        events.forEach(({ element, event, handler }) => {
            element.addEventListener(event, handler)
            listenersRef.current.push({ element, event, handler })
        })
    }

    const updateDocumentTitle = (title: string) => {
        document.title = title
    }

    useEffect(() => {
        return () => stopFlashing()
    }, [])

    return { flashTitle, stopFlashing, updateDocumentTitle }
}
